import { createContext, useContext, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { api } from "@/api/apiSlice";
import { getBackgroundJobList } from "@/api/background-job/getBackgroundJobList";
import { isBackgroundJobActive } from "@/api/background-job/_models/BackgroundJob";
import type { BackgroundJob, BackgroundJobStatus } from "@/api/background-job/_models/BackgroundJob";
import type { ReportSnapshotDto } from "@/api/dtos";
import { InventoryTags } from "@/api/inventory/InventoryTags";
import { ReportTags } from "@/api/report/ReportTags";
import { deleteReportUpload } from "@/api/report/deleteReportUpload";
import { getReportSnapshot } from "@/api/report/getReportSnapshot";
import { getReportUploadList } from "@/api/report/getReportUploadList";
import { isStockReportKind } from "@/api/report/_models/ReportUpload";
import type { ReportSnapshotSummary, ReportUploadedFile, ReportUploadKind } from "@/api/report/_models/ReportUpload";
import { useAppDispatch } from "@/app/hooks";
import type { SnapshotDropzoneExistingFiles } from "./snapshot-dropzone/snapshotDropzone.utils";
import { useSnapshot } from "./snapshot.context";
import type { SnapshotBuildStart } from "./snapshot.context";
import {
  SNAPSHOT_BUILD_JOB_KIND,
  toErrorText,
  toSnapshotBuildSubject,
  toSnapshotSectionDuplicates,
  toSnapshotSectionFiles,
  toSnapshotSectionStatus,
} from "./snapshot.utils";
import type { SnapshotSectionDuplicates, SnapshotSectionFiles, SnapshotSectionStatus } from "./snapshot.utils";
import type { SnapshotUploadFile } from "./useSnapshotUpload";

/** What a section shows about its build: running, why it failed, or what it built. */
export interface SnapshotSectionBuild {
  isBuilding: boolean;
  error: string | null;
  result: ReportSnapshotDto | null;
}

/** Deleting one of the section's files from S3; a failed delete is a toast and the confirm stays open. */
export interface SnapshotSectionDelete {
  /** The file being asked about (its row shows the inline confirm); null when none. */
  file: ReportUploadedFile | null;
  isDeleting: boolean;
  ask: (file: ReportUploadedFile) => void;
  cancel: () => void;
  confirm: () => void;
}

/** One report's section on a Snapshot page: the page's state bound to this report, plus the section's own queries. */
export interface SnapshotSectionState {
  report: ReportUploadKind;
  /** Its name on screen ("Toll"). */
  label: string;
  /** The day as yyMMdd. */
  date: string;
  dateLabel: string;
  /** Files dropped here. */
  files: SnapshotUploadFile[];
  /** The day's files in S3. */
  existing: SnapshotDropzoneExistingFiles;
  /** Dropped and stored files as the section lists them. */
  sectionFiles: SnapshotSectionFiles;
  /** The day's snapshot as stored: null = not built; undefined = loading. */
  builtSnapshot: ReportSnapshotSummary | null | undefined;
  build: SnapshotSectionBuild;
  /** Files to build from, none still uploading, not building. */
  canBuild: boolean;
  /** Nothing is dropped, deleted or rebuilt while a build reads the files. */
  isBuilding: boolean;
  status: SnapshotSectionStatus;
  /** The day's files (S3 keys) uploaded after its snapshot was built. */
  newSinceBuildKeys: string[];
  /** The day's files that share a name. */
  duplicates: SnapshotSectionDuplicates;
  startBuild: () => void;
  addFiles: (files: File[]) => void;
  retryFile: (id: string) => void;
  removeFile: (id: string) => void;
  deletion: SnapshotSectionDelete;
}

/** A Snapshot section's state, provided by SnapshotSectionProvider; read with useSnapshotSection. */
export const SnapshotSectionContext = createContext<SnapshotSectionState | null>(null);

const toSnapshotSectionBuild = (start: SnapshotBuildStart, job: BackgroundJob | undefined): SnapshotSectionBuild => {
  if (start.isStarting || isBackgroundJobActive(job)) return { isBuilding: true, error: null, result: null };
  if (start.error) return { isBuilding: false, error: start.error, result: null };
  if (job?.status === "FAILED") return { isBuilding: false, error: job.error ?? "The build failed", result: null };
  if (job?.status === "DONE") return { isBuilding: false, error: null, result: job.result as ReportSnapshotDto };
  return { isBuilding: false, error: null, result: null };
};

const BUILD_POLL_MS = 2000;

interface SnapshotSectionProviderProps {
  report: ReportUploadKind;
  label: string;
  children: ReactNode;
}

/**
 * Owns one report's section on a Snapshot page: its files in S3 for the day, its built snapshot,
 * its latest build job (polled while running) and the delete flow. Reports its summary to the page,
 * which is what "Build all" builds.
 */
export const SnapshotSectionProvider = (props: SnapshotSectionProviderProps) => {
  const { report, label, children } = props;
  const snapshot = useSnapshot();
  const date = snapshot.date;
  const files = snapshot.uploads[report].files;
  const setSectionSummary = snapshot.setSectionSummary;
  const notify = snapshot.notify;
  const dispatch = useAppDispatch();
  const [fileToDelete, setFileToDelete] = useState<ReportUploadedFile | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [requestDelete, deleteState] = deleteReportUpload.useMutation();
  const uploadListQuery = getReportUploadList.useQuery({ report, date });
  const snapshotQuery = getReportSnapshot.useQuery({ report, date });
  const jobArgs = { kind: SNAPSHOT_BUILD_JOB_KIND, subject: toSnapshotBuildSubject(report, date), limit: 1 };
  const jobQuery = getBackgroundJobList.useQuery(jobArgs);
  const job = jobQuery.data?.[0];
  const isJobActive = isBackgroundJobActive(job);
  getBackgroundJobList.useQuerySubscription(jobArgs, { pollingInterval: isJobActive ? BUILD_POLL_MS : 0 });
  let build = toSnapshotSectionBuild(snapshot.buildStarts[report], job);
  if (isRefreshing) build = { isBuilding: true, error: null, result: null };
  const previousStatus = useRef<BackgroundJobStatus | undefined>(job?.status);
  const refetchSnapshot = snapshotQuery.refetch;
  const existing: SnapshotDropzoneExistingFiles = {
    files: uploadListQuery.data ?? [],
    isLoading: uploadListQuery.isLoading,
    error: uploadListQuery.error,
  };
  const hasFiles = existing.files.length > 0 || files.some((file) => file.status === "uploaded");
  const isUploading = files.some((file) => file.status === "uploading");
  const hasSource = hasFiles || isStockReportKind(report);
  const canBuild = hasSource && !isUploading && !build.isBuilding;
  const sectionFiles = toSnapshotSectionFiles(existing.files, files);
  const builtAt = snapshotQuery.data?.builtAt ?? null;
  const status = toSnapshotSectionStatus({
    isBuilding: build.isBuilding,
    buildError: build.error,
    builtAt,
    fileCount: sectionFiles.fileCount,
    latestUploadAt: sectionFiles.latestUploadAt,
  });
  const newSinceBuildKeys: string[] = [];
  if (builtAt) {
    for (const file of existing.files) {
      if (file.uploadedAt > builtAt) newSinceBuildKeys.push(file.key);
    }
  }
  const needsBuild = canBuild && (status === "none" || status === "stale" || status === "failed");

  const handleDeleteConfirm = async () => {
    if (!fileToDelete) return;
    try {
      await requestDelete({ report, uploadId: fileToDelete.id, date }).unwrap();
      setFileToDelete(null);
      snapshot.markFileDeleted(report, fileToDelete.key);
      notify(`Deleted ${fileToDelete.filename}`);
    }
    catch (e) {
      notify(`Couldn't delete ${fileToDelete.filename}: ${toErrorText(e)}`, "error");
    }
  };

  const deletion: SnapshotSectionDelete = {
    file: fileToDelete,
    isDeleting: deleteState.isLoading,
    ask: (file) => setFileToDelete(file),
    cancel: () => setFileToDelete(null),
    confirm: () => {
      void handleDeleteConfirm();
    },
  };

  const section: SnapshotSectionState = {
    report,
    label,
    date,
    dateLabel: snapshot.dateLabel,
    files,
    existing,
    sectionFiles,
    builtSnapshot: snapshotQuery.data,
    build,
    canBuild,
    isBuilding: build.isBuilding,
    status,
    newSinceBuildKeys,
    duplicates: toSnapshotSectionDuplicates(existing.files),
    startBuild: () => {
      if (canBuild) snapshot.build([report]);
    },
    addFiles: (dropped) => {
      snapshot.addFiles(report, dropped);
      const count = dropped.length;
      notify(`Added ${count} file${count === 1 ? "" : "s"} to ${label}`);
    },
    retryFile: (id) => snapshot.retryFile(report, id),
    removeFile: (id) => snapshot.removeFile(report, id),
    deletion,
  };

  const fileCount = sectionFiles.fileCount;
  useEffect(() => {
    setSectionSummary(report, { label, fileCount, status, canBuild, needsBuild });
  }, [setSectionSummary, report, label, fileCount, status, canBuild, needsBuild]);

  useEffect(() => {
    return () => setSectionSummary(report, null);
  }, [setSectionSummary, report]);

  const dateLabel = snapshot.dateLabel;
  const jobError = job?.error;
  useEffect(() => {
    const before = previousStatus.current;
    previousStatus.current = job?.status;
    const wasActive = before === "QUEUED" || before === "RUNNING";
    if (!wasActive) return;
    if (job?.status === "FAILED") {
      notify(`${label} snapshot failed: ${jobError ?? "unknown error"}`, "error");
      return;
    }
    if (job?.status !== "DONE") return;
    const refreshSnapshot = async () => {
      setIsRefreshing(true);
      try {
        await refetchSnapshot();
      }
      finally {
        setIsRefreshing(false);
      }
      notify(`${label} snapshot built for ${dateLabel}`);
    };
    void refreshSnapshot();
    dispatch(api.util.invalidateTags([
      { type: ReportTags.ToeReport, id: "LIST" },
      { type: ReportTags.TollReport, id: "LIST" },
      { type: ReportTags.EstoreReport, id: "LIST" },
      { type: InventoryTags.Inventory, id: "LIST" },
    ]));
  }, [dispatch, job?.status, refetchSnapshot, notify, label, dateLabel, jobError]);

  return (
    <SnapshotSectionContext.Provider value={section}>
      {children}
    </SnapshotSectionContext.Provider>
  );
};

/** The Snapshot section's state around this component; throws outside a SnapshotSectionProvider. */
export const useSnapshotSection = (): SnapshotSectionState => {
  const section = useContext(SnapshotSectionContext);
  if (!section) throw new Error("useSnapshotSection must be used inside a Snapshot section");
  return section;
};
